import useGetFormFields from '@hooks/useGetFormFields';
import {
  FormBuilder,
  FormBuilderContainer,
  Field,
  GenericObj,
  getDefaultValue,
  NestedObject,
} from '@gemini/lux-ds-pms';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  flattenFields,
  getDefaultFieldValue,
  normalizeFormValues,
  shouldNormalizeFieldValue,
} from './utils';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import {
  CountryListDTO,
  InsuranceDTO,
  MasterLookUpDTO,
} from '@utils/interfaces';

import { Divider, Stack } from '@gemini/fe-ds-core';
import { UseFormReturn } from 'react-hook-form';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { deepEqual, mergeObjects } from '@utils/functions';
import { FormWrapper } from './FormBuilderStyles';
import {
  getClearedImportedContactDetails,
  shouldClearImportedContactDetails,
} from './contactDetailsImport.utils';
import { PatientPolicyDTO } from '@hooks/useGetPatientPolicies';

interface FormBuilderObProps {
  formIds: Array<FormIds>;
  setEnableButton: (arg: boolean) => void;
  onUnmount?: (formData?: NestedObject) => void;
  onChange?: (formData?: GenericObj, fieldName?: string) => void;
  needUpdates?: boolean;
  optionsList?: Array<FormBuilderObOptions>;
  patientIndex?: number;
  isVisible?: boolean;
  resetFieldsContactsDetails?: boolean;
  mykey?: string;
}

export interface FormBuilderObOptions {
  insurances?: InsuranceDTO;
  countryList?: CountryListDTO;
  policies?: PatientPolicyDTO[];
  countryStatesList?: CountryListDTO;
  genderList?: MasterLookUpDTO;
  holders?: MasterLookUpDTO;
  readOnlyPrefilledPatientsDetailsFields?: boolean;
  prefilledPatientDetailsFields?: {
    firstName?: boolean;
    lastName?: boolean;
    dateOfBirth?: boolean;
    email?: boolean;
    phoneNumber?: boolean;
  };
  hideInsuranceDropdown?: boolean;
}

const hasPatientDetailsValue = (value?: unknown) =>
  value !== undefined &&
  value !== null &&
  (typeof value !== 'string' || value.trim() !== '');

const hasStoredPatientDetailsValues = (patientDetails?: GenericObj) =>
  [
    patientDetails?.firstName,
    patientDetails?.lastName,
    patientDetails?.dateOfBirth,
    patientDetails?.gender,
    patientDetails?.email,
    patientDetails?.phone?.number,
    patientDetails?.streetAddress,
    patientDetails?.city,
    patientDetails?.zipCode,
    patientDetails?.state,
  ].some(hasPatientDetailsValue);

/**
 * Recupera le informazioni da servizio e passa i dati al FormBuilder.
 */
const FormBuilderOb = ({
  formIds,
  setEnableButton,
  onUnmount = () => {},
  onChange = () => {},
  needUpdates = false,
  optionsList,
  patientIndex = 0,
  isVisible = true,
  resetFieldsContactsDetails,
  mykey,
}: FormBuilderObProps) => {
  const getCurrentPatient = usePatientStore(state => state.getCurrentPatient);
  const userInfo = useOnlineBookingStore(state => state.infoUser);
  const brandNameFromStore = useOnlineBookingStore(state => state.brandName);
  const setCheckboxValue = useOnlineBookingStore(
    state => state.setContactDetailsCheckboxStatus,
  );
  const patients = usePatientStore(state => state.patients);
  const contactsDetails = usePatientStore(state => state.contactsDetails);
  const methods = useRef<UseFormReturn<GenericObj> | null>(null);
  const [formIsReady, setFormIsReady] = useState(false);
  const datasIsSet = useRef(false);
  const skipClearOnNextCheckboxOff = useRef(false);
  const previousResetFieldsContactsDetails = useRef(resetFieldsContactsDetails);
  const [formDefValues, setFormDefValues] = useState<GenericObj | undefined>();
  const [formDefValuesList, setFormDefValuesList] = useState<
    Array<GenericObj> | undefined
  >();
  const patientToImport = useMemo(() => {
    const availablePatients = patients.filter(patient => patient);
    if (!availablePatients.length) return undefined;

    const firstPatientOrder = Math.min(
      ...availablePatients.map(patient =>
        typeof patient?.order !== 'undefined' ? patient.order : 99,
      ),
    );

    return (
      availablePatients.find(patient => patient?.order === firstPatientOrder) ||
      availablePatients[0]
    );
  }, [patients]);

  const patientDetailsToImport = patientToImport?.patientDetails;

  const importedContactDetails = useMemo(
    () => ({
      firstName: patientDetailsToImport?.firstName,
      lastName: patientDetailsToImport?.lastName,
      phone: patientDetailsToImport?.phone,
      email: patientDetailsToImport?.email,
      dateOfBirth: patientDetailsToImport?.dateOfBirth,
    }),
    [
      patientDetailsToImport?.dateOfBirth,
      patientDetailsToImport?.email,
      patientDetailsToImport?.firstName,
      patientDetailsToImport?.lastName,
      patientDetailsToImport?.phone,
    ],
  );

  const importedContactDetailsForDefaultValues = useMemo(
    () =>
      Object.entries(importedContactDetails).reduce((acc, [key, value]) => {
        if (typeof value !== 'undefined') {
          acc[key] = value;
        }
        return acc;
      }, {} as GenericObj),
    [importedContactDetails],
  );

  const datas = useGetFormFields({
    formIds,
    optionsList,
    patientIndex,
  });

  const sanitizeCurrentFormValues = useCallback(() => {
    if (!methods.current || !datas) return false;

    let hasNormalizedValues = false;

    const sanitizeFields = (fields: Field[]) => {
      fields.forEach(field => {
        if (field.children) {
          sanitizeFields(field.children);
          return;
        }

        const fieldValue = methods.current?.getValues(field.id);

        if (shouldNormalizeFieldValue(fieldValue, field.type)) {
          methods.current?.setValue(
            field.id,
            getDefaultFieldValue(field.type),
            {
              shouldValidate: true,
            },
          );
          hasNormalizedValues = true;
        }
      });
    };

    datas.forEach(({ data }) => sanitizeFields(data));

    return hasNormalizedValues;
  }, [datas]);

  useEffect(() => {
    if (datas && !datasIsSet.current) {
      console.log('#datas: ', datas, formIds, patientIndex);
      datasIsSet.current = true;
      // setConfigForm(datas);
      const patient = getCurrentPatient(patientIndex);
      // console.log('#patient: ', patient);
      const objs = formIds.map((formId, index) => {
        console.log('#FormbuilderOB | formId', formId);
        const defaultContactDetails =
          formId === FormIds.ContactInformation && resetFieldsContactsDetails
            ? {
                contactsDetails: {
                  ...contactsDetails,
                  ...importedContactDetailsForDefaultValues,
                },
              }
            : { contactsDetails: contactsDetails };
        const flatten: GenericObj = flattenFields(
          datas[index].data,
          formId === FormIds.ContactInformation
            ? defaultContactDetails
            : (patient as any),
        );

        if (formId === FormIds.PatientDetail) {
          flatten.patientDetails.country = 'US';
          flatten.patientDetails.phone.type = 'mobile';

          if (userInfo) {
            // by default set phone number prefix and country (only if not already set)
            if (!flatten.patientDetails.phone?.number) {
              flatten.patientDetails.phone = {
                ...flatten.patientDetails.phone,
                prefix: userInfo?.phoneNumber?.prefix,
                type: userInfo?.phoneNumber?.type,
                country: userInfo?.phoneNumber?.country,
              };
            }

            const shouldPrefillPatientDetailsFromUserInfo =
              patientIndex === 0 &&
              !hasStoredPatientDetailsValues(patient?.patientDetails);

            // If is first patient and there are no user patientDetails in store, prefill with userInfoFromWCS
            if (shouldPrefillPatientDetailsFromUserInfo) {
              flatten.patientDetails.firstName = userInfo?.firstName;
              flatten.patientDetails.lastName = userInfo?.lastName;
              flatten.patientDetails.email = userInfo?.email;
              flatten.patientDetails.dateOfBirth = userInfo?.dateOfBirth;
              flatten.patientDetails.gender = userInfo?.gender;
              flatten.patientDetails.streetAddress = userInfo?.streetAddress;
              flatten.patientDetails.city = userInfo?.city;
              flatten.patientDetails.zipCode = userInfo?.zipCode;
              flatten.patientDetails.state = userInfo?.state;
              flatten.patientDetails.country =
                userInfo?.country || flatten.patientDetails.country;
              flatten.patientDetails.phone = {
                ...flatten.patientDetails.phone,
                number:
                  typeof userInfo?.phoneNumber?.number === 'number'
                    ? userInfo.phoneNumber.number.toString()
                    : undefined,
              };
            }
          }
        }
        if (formId === FormIds.ContactInformation) {
          flatten.contactsDetails.phone.type = 'mobile';
        }
        normalizeFormValues(datas[index].data, flatten);
        console.log('#FormbuilderOB | flatten', flatten);
        return flatten;
      });
      // setDefaultValues(objs);
      const res = objs.reduce((prev, curr) => {
        return mergeObjects(prev, curr, true);
      }, {}) as GenericObj;
      datas.forEach(({ data }) => normalizeFormValues(data, res));
      // if (res && formIds.includes(FormIds.PatientDetail)) {
      //   res.patientDetails.country = 'US';
      // }
      setFormDefValues(res);
      setFormDefValuesList(objs);
      console.log('#objs: ', objs, patientIndex);
      console.log('#res: ', res, patientIndex);
    }
  }, [datas]);

  useEffect(() => {
    if (formIsReady) {
      sanitizeCurrentFormValues();
    }
  }, [formIsReady, sanitizeCurrentFormValues]);

  useEffect(() => {
    console.log('#ImportFlow | start');
    const previousValue = previousResetFieldsContactsDetails.current;
    previousResetFieldsContactsDetails.current = resetFieldsContactsDetails;
    const shouldClearImportedValues = shouldClearImportedContactDetails({
      previousCheckboxValue: previousValue,
      currentCheckboxValue: resetFieldsContactsDetails,
      skipClearOnNextCheckboxOff: skipClearOnNextCheckboxOff.current,
    });

    if (
      formIsReady &&
      methods.current &&
      formIds.includes(FormIds.ContactInformation) &&
      previousValue &&
      resetFieldsContactsDetails === false
    ) {
      if (skipClearOnNextCheckboxOff.current) {
        skipClearOnNextCheckboxOff.current = false;
      } else if (shouldClearImportedValues) {
        const clearedValues = getClearedImportedContactDetails();
        const fieldsToClear = [
          ['contactsDetails.firstName', clearedValues.firstName],
          ['contactsDetails.lastName', clearedValues.lastName],
          ['contactsDetails.email', clearedValues.email],
          ['contactsDetails.dateOfBirth', clearedValues.dateOfBirth],
          ['contactsDetails.phone.number', clearedValues.phone],
        ] as const;
        fieldsToClear.forEach(([fieldName, fieldValue]) => {
          methods.current?.setValue(fieldName, fieldValue, {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
          });
        });
        void methods.current.trigger(
          fieldsToClear.map(([fieldName]) => fieldName),
        );
      }
    }

    if (
      formIsReady &&
      methods.current &&
      typeof resetFieldsContactsDetails !== 'undefined' &&
      resetFieldsContactsDetails &&
      patientDetailsToImport &&
      formIds.includes(FormIds.ContactInformation)
    ) {
      console.log('#ImportFlow|FormBuilderOb.patientDetails...');

      // Estrai i dettagli del paziente
      const { firstName, lastName, phone, email, dateOfBirth } =
        importedContactDetails;

      // Imposta i valori individualmente
      methods.current.setValue('contactsDetails.firstName', firstName || '', {
        shouldValidate: true,
      });
      methods.current.setValue('contactsDetails.lastName', lastName || '', {
        shouldValidate: true,
      });
      methods.current.setValue(
        'contactsDetails.phone.number',
        phone?.number || '',
        {
          shouldValidate: true,
        },
      );
      methods.current.setValue(
        'contactsDetails.phone.prefix',
        phone?.prefix || '',
        {
          shouldValidate: true,
        },
      );
      methods.current.setValue(
        'contactsDetails.phone.type',
        phone?.type || 'mobile',
        {
          shouldValidate: true,
        },
      );
      methods.current.setValue('contactsDetails.email', email || '', {
        shouldValidate: true,
      });
      methods.current.setValue(
        'contactsDetails.dateOfBirth',
        dateOfBirth || '',
        {
          shouldValidate: true,
        },
      );

      const importedFormValues = {
        contactsDetails: importedContactDetails,
      };

      const subscription = methods.current.watch((value, { name }) => {
        // controlla che il valore sia diverso da quello inizialmente impostato
        if (
          name &&
          !deepEqual(
            getDefaultValue(name, value),
            getDefaultValue(name, importedFormValues),
          )
        ) {
          skipClearOnNextCheckboxOff.current = true;
          setCheckboxValue(false);
          subscription.unsubscribe();
        }
      });
      return () => subscription.unsubscribe();
    }
    if (methods.current) {
      const subscription = methods.current.watch((value, { name }) => {
        if (sanitizeCurrentFormValues()) {
          return;
        }

        if (name) {
          onChange(value as GenericObj, name);
        }
      });
      return () => subscription.unsubscribe();
    }
    return;
  }, [
    resetFieldsContactsDetails,
    formIsReady,
    formIds,
    getCurrentPatient,
    patientDetailsToImport,
    patientIndex,
    importedContactDetails,
    onChange,
    sanitizeCurrentFormValues,
  ]);

  console.log({ optionsList });
  const key = useMemo(() => `OBContainer_${mykey}`, []);

  return (
    <FormWrapper isVisible={isVisible} brandName={!!brandNameFromStore}>
      {formDefValues && formDefValuesList && methods && (
        <FormBuilderContainer
          optionForm={{
            defaultValues: formDefValues,
            mode: brandNameFromStore ? 'onBlur' : 'onChange',
          }}
          onUnmount={onUnmount}
          setEnableButton={setEnableButton}
          myRef={methods}
          setFormIsReady={setFormIsReady}
          key={key}
          mykey={key}
        >
          {formIds.map((formId, index) => {
            return (
              <>
                <FormBuilder
                  mykey={`${key}|FormBuilder_${formId}`}
                  key={`${key}|FormBuilder_${formId}`}
                  form={datas[index].data}
                  defaultValues={formDefValuesList[index]}
                  needUpdates={needUpdates}
                />
                {index < formIds.length - 1 &&
                  datas[index].data.some(
                    d =>
                      (!d.children && d.visibility) ||
                      d.children?.some(c => c.visibility),
                  ) && (
                    <Stack sx={{ padding: '0 1rem' }}>
                      <Divider />
                    </Stack>
                  )}
              </>
            );
          })}
        </FormBuilderContainer>
      )}
    </FormWrapper>
  );
};

export default FormBuilderOb;
